import React, { useEffect, useMemo, useRef, useState } from 'react';
import superjson from 'superjson';
import type { CoverLayout, CoverTextLayer, Panel } from '../helpers/coverLayout';
import { renderPaperbackCoverFromLayout, renderEbookCoverFromLayout } from '../helpers/coverRender';
import styles from './CoverEditor.module.css';

interface Props {
  bookId: string;
}

const FONTS: Array<{ id: string; label: string; stack: string }> = [
  { id: 'georgia',          label: 'Georgia',          stack: '"Georgia", "Times New Roman", serif' },
  { id: 'garamond',         label: 'Garamond',         stack: '"EB Garamond", "Garamond", serif' },
  { id: 'playfair',         label: 'Playfair Display', stack: '"Playfair Display", "Georgia", serif' },
  { id: 'crimson',          label: 'Crimson Text',     stack: '"Crimson Text", "Georgia", serif' },
  { id: 'cormorant',        label: 'Cormorant',        stack: '"Cormorant Garamond", "Garamond", serif' },
  { id: 'libre-baskerville',label: 'Libre Baskerville',stack: '"Libre Baskerville", "Georgia", serif' },
  { id: 'merriweather',     label: 'Merriweather',     stack: '"Merriweather", "Georgia", serif' },
  { id: 'lora',             label: 'Lora',             stack: '"Lora", "Georgia", serif' },
  { id: 'spectral',         label: 'Spectral',         stack: '"Spectral", "Georgia", serif' },
  { id: 'inter',            label: 'Inter',            stack: '"Inter", "Helvetica Neue", "Arial", sans-serif' },
];

type Mode = 'wrap' | 'front' | 'ebook';

export function CoverEditor({ bookId }: Props) {
  const [layout, setLayout] = useState<CoverLayout | null>(null);
  const [outline, setOutline] = useState<any>(null);
  const [pageCount, setPageCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>('wrap');
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState<'idle' | 'queued' | 'done'>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    layerId: string;
    panelEl: HTMLElement;
    startX: number;
    startY: number;
    origLayer: CoverTextLayer;
    mode: 'move' | 'resize-br';
  } | null>(null);

  // Load layout + outline
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/_api/taskingbot/book-cover-layout?bookId=${encodeURIComponent(bookId)}`, { credentials: 'include' })
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) throw new Error(text || `Failed (${r.status})`);
        try { return superjson.parse(text) as any; } catch { return JSON.parse(text); }
      })
      .then((data) => {
        if (cancelled) return;
        setLayout(data.layout);
        setOutline(data.outline);
        setPageCount(data.pageCount || data.layout?.pageCount || 0);
      })
      .catch((err) => !cancelled && setError(err?.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  // Derived: format/spine
  const trimW = 6;
  const trimH = 9;
  const bleed = 0.125;
  const spineIn = layout?.spineIn ?? 0.06;
  const wrapWidthIn = trimW * 2 + spineIn + bleed * 2;
  const wrapHeightIn = trimH + bleed * 2;
  const SCALE = mode === 'wrap' ? 60 : mode === 'front' ? 80 : 50; // px per inch on screen
  const canvasWidthPx = (mode === 'wrap' ? wrapWidthIn : mode === 'front' ? 6.25 : 5) * SCALE;
  const canvasHeightPx = (mode === 'wrap' ? wrapHeightIn : mode === 'front' ? 9.25 : 8) * SCALE;

  const selectedLayer = useMemo(
    () => layout?.layers.find((l) => l.id === selectedLayerId) || null,
    [layout, selectedLayerId],
  );

  const updateLayout = (mut: (prev: CoverLayout) => CoverLayout) => {
    setLayout((prev) => (prev ? mut(prev) : prev));
  };

  const updateLayer = (id: string, patch: Partial<CoverTextLayer>) => {
    updateLayout((prev) => ({
      ...prev,
      layers: prev.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
  };

  const updateLayerRef = useRef(updateLayer);
  updateLayerRef.current = updateLayer;

  // ── Drag/resize ──
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const ds = dragState.current;
      if (!ds) return;
      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;
      const panelRect = ds.panelEl.getBoundingClientRect();
      const fx = dx / panelRect.width;
      const fy = dy / panelRect.height;
      if (ds.mode === 'move') {
        const newX = Math.max(0, Math.min(1, ds.origLayer.x + fx));
        const newY = Math.max(0, Math.min(1, ds.origLayer.y + fy));
        updateLayerRef.current(ds.layerId, { x: newX, y: newY });
      } else if (ds.mode === 'resize-br') {
        const newW = Math.max(0.05, Math.min(1, ds.origLayer.w + fx * 2));
        const newH = Math.max(0.02, Math.min(1, ds.origLayer.h + fy * 2));
        updateLayerRef.current(ds.layerId, { w: newW, h: newH });
      }
    };
    const onUp = () => {
      dragState.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleLayerMouseDown = (e: React.MouseEvent, layerId: string, panelEl: HTMLElement | null, dragMode: 'move' | 'resize-br') => {
    if (!panelEl || !layout) return;
    const layer = layout.layers.find((l) => l.id === layerId);
    if (!layer) return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedLayerId(layerId);
    dragState.current = {
      layerId,
      panelEl,
      startX: e.clientX,
      startY: e.clientY,
      origLayer: { ...layer },
      mode: dragMode,
    };
  };

  // ── Save ──
  const handleSave = async (regenerate = false) => {
    if (!layout) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/_api/taskingbot/book-cover-layout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ bookId, layout, regenerate }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || `Save failed (${res.status})`);
      setSavedAt(new Date().toLocaleTimeString());
      if (regenerate) setRegen('queued');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Image upload (replaces background) ──
  const handleBgUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result || '').split(',')[1];
      if (!b64) return;
      try {
        const res = await fetch('/_api/taskingbot/book-cover-bg-upload', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: superjson.stringify({ bookId, mime: file.type, base64: b64 }),
        });
        const text = await res.text();
        if (!res.ok) throw new Error(text || 'Upload failed');
        const data = superjson.parse(text) as any;
        if (data?.backgroundUrl) {
          updateLayout((prev) => ({ ...prev, backgroundUrl: data.backgroundUrl }));
        }
      } catch (err: any) {
        setError(err?.message || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  // ── Background regeneration ──
  const handleRegenBg = async () => {
    try {
      const res = await fetch('/_api/taskingbot/book-cover-bg-regen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ bookId }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || 'Regen failed');
      const data = superjson.parse(text) as any;
      if (data?.backgroundUrl) {
        updateLayout((prev) => ({ ...prev, backgroundUrl: data.backgroundUrl }));
      }
    } catch (err: any) {
      setError(err?.message || 'Regen failed');
    }
  };

  // ── KDP publish ──
  const handlePublish = async (format: 'paperback' | 'hardcover' | 'ebook') => {
    try {
      await handleSave(true); // ensure latest layout is rendered first
      const res = await fetch('/_api/taskingbot/kdp-publish', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ bookId, format }),
      });
      const text = await res.text();
      const data = (() => { try { return superjson.parse(text) as any; } catch { return JSON.parse(text); } })();
      if (!res.ok) throw new Error(data?.error || `KDP publish failed (${res.status})`);
      if (data?.needsOtp) {
        const otp = window.prompt('Amazon sent a one-time code. Enter it:');
        if (!otp) return;
        const r2 = await fetch('/_api/taskingbot/kdp-publish', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: superjson.stringify({ bookId, format, otp, otpSessionId: data.otpSessionId }),
        });
        const t2 = await r2.text();
        const d2 = (() => { try { return superjson.parse(t2) as any; } catch { return JSON.parse(t2); } })();
        if (!r2.ok) throw new Error(d2?.error || 'KDP publish failed (after OTP)');
        if (d2?.draftUrl) window.open(d2.draftUrl, '_blank');
      } else if (data?.draftUrl) {
        window.open(data.draftUrl, '_blank');
      }
    } catch (err: any) {
      setError(err?.message || 'Publish failed');
    }
  };

  if (loading) return <div className={styles.empty}>Loading cover…</div>;
  if (error && !layout) return <div className={styles.empty}>Error: {error}</div>;
  if (!layout) return <div className={styles.empty}>No layout</div>;

  // ── Render the canvas using the same logic as the print renderer ──
  // For the editor canvas, we lay out panels visually in a row (wrap mode) or
  // single-panel (front/ebook). Layers within each panel are absolute-positioned.
  const renderPanel = (panel: Panel, widthIn: number, heightIn: number) => {
    return (
      <div
        className={styles.panel}
        data-panel={panel}
        style={{ width: `${widthIn * SCALE}px`, height: `${heightIn * SCALE}px` }}
        onMouseDown={() => setSelectedLayerId(null)}
      >
        {layout.backgroundUrl && (
          <img
            src={layout.backgroundUrl}
            alt=""
            className={styles.bgImg}
            style={{
              objectPosition:
                panel === 'back' ? 'left center' : panel === 'spine' ? 'center center' : 'right center',
            }}
          />
        )}
        <div className={styles.bgOverlay} style={{ background: layout.overlayColor, opacity: layout.overlayOpacity }} />
        {panel === 'back' && layout.showBarcodeZone && (
          <div className={styles.barcodeZone}>ISBN / Barcode</div>
        )}
        {layout.layers
          .filter((l) => l.panel === panel)
          .map((L) => {
            const isSel = L.id === selectedLayerId;
            const leftFr = (L.x - L.w / 2) * 100;
            const topFr = (L.y - L.h / 2) * 100;
            const wFr = L.w * 100;
            const hFr = L.h * 100;
            const sizePx = L.size * (SCALE / 72); // pt → px on canvas
            const shadow = L.shadow
              ? `${L.shadow.y || 0}px ${L.shadow.y || 0}px ${L.shadow.blur}px ${L.shadow.color}`
              : 'none';
            return (
              <div
                key={L.id}
                className={`${styles.layer} ${isSel ? styles.layerSelected : ''}`}
                style={{
                  left: `${leftFr}%`,
                  top: `${topFr}%`,
                  width: `${wFr}%`,
                  height: `${hFr}%`,
                  fontFamily: L.font,
                  fontSize: `${sizePx}px`,
                  fontWeight: L.weight,
                  fontStyle: L.italic ? 'italic' : 'normal',
                  color: L.color,
                  textAlign: L.align as any,
                  letterSpacing: `${L.letterSpacing}em`,
                  lineHeight: L.lineHeight,
                  textShadow: shadow,
                  transform: L.rotate ? `rotate(${L.rotate}deg)` : undefined,
                  display: L.kind === 'blurb' ? 'block' : 'flex',
                  alignItems: 'center',
                  justifyContent:
                    L.align === 'center' ? 'center' : L.align === 'right' ? 'flex-end' : 'flex-start',
                }}
                onMouseDown={(e) => {
                  const panelEl = (e.currentTarget.parentElement as HTMLElement) || null;
                  handleLayerMouseDown(e, L.id, panelEl, 'move');
                }}
              >
                {L.text}
                {isSel && (
                  <span
                    className={styles.resizeHandle}
                    onMouseDown={(e) => {
                      const panelEl = (e.currentTarget.parentElement?.parentElement as HTMLElement) || null;
                      handleLayerMouseDown(e, L.id, panelEl, 'resize-br');
                    }}
                  />
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className={styles.editor}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <strong className={styles.bookTitle}>{outline?.title || 'Book'}</strong>
          <span className={styles.bookMeta}>{pageCount} pages · {layout.format || 'paperback'} · spine {spineIn.toFixed(3)}"</span>
        </div>
        <div className={styles.toolbarCenter}>
          <button className={mode === 'wrap' ? styles.tabActive : styles.tab} onClick={() => setMode('wrap')}>Wrap</button>
          <button className={mode === 'front' ? styles.tabActive : styles.tab} onClick={() => setMode('front')}>Front</button>
          <button className={mode === 'ebook' ? styles.tabActive : styles.tab} onClick={() => setMode('ebook')}>Ebook</button>
        </div>
        <div className={styles.toolbarRight}>
          <button className={styles.btn} onClick={() => handleSave(false)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className={styles.btnPrimary} onClick={() => handleSave(true)} disabled={saving}>
            Save + Regenerate
          </button>
          <span className={styles.savedAt}>{savedAt && `saved ${savedAt}`}</span>
        </div>
      </div>

      {error && <div className={styles.errorBar}>{error}</div>}
      {regen === 'queued' && (
        <div className={styles.infoBar}>
          Cover regeneration queued. The PDF + ebook JPG URLs in your chat will update in ~30 seconds.
        </div>
      )}

      <div className={styles.workspace}>
        {/* ── Canvas ── */}
        <div className={styles.canvasWrap}>
          <div
            ref={canvasRef}
            className={styles.canvas}
            style={{ width: `${canvasWidthPx}px`, height: `${canvasHeightPx}px` }}
          >
            {mode === 'wrap' && (
              <div className={styles.wrapRow}>
                {renderPanel('back', trimW + bleed, wrapHeightIn)}
                {renderPanel('spine', spineIn, wrapHeightIn)}
                {renderPanel('front', trimW + bleed, wrapHeightIn)}
                {/* Bleed guides */}
                <div className={styles.bleedGuide} style={{ left: `${bleed * SCALE}px`, top: `${bleed * SCALE}px`, width: `${(wrapWidthIn - 2 * bleed) * SCALE}px`, height: `${(wrapHeightIn - 2 * bleed) * SCALE}px` }} />
                <div className={styles.spineGuideLeft} style={{ left: `${(trimW + bleed) * SCALE}px`, height: `${wrapHeightIn * SCALE}px` }} />
                <div className={styles.spineGuideRight} style={{ left: `${(trimW + bleed + spineIn) * SCALE}px`, height: `${wrapHeightIn * SCALE}px` }} />
              </div>
            )}
            {mode === 'front' && renderPanel('front', 6.25, 9.25)}
            {mode === 'ebook' && (
              <div className={styles.canvas} style={{ width: `${canvasWidthPx}px`, height: `${canvasHeightPx}px`, backgroundImage: layout.backgroundUrl ? `url('${layout.backgroundUrl}')` : 'none', backgroundSize: 'cover', backgroundPosition: 'right center' }}>
                {/* Front-only layers, but proportioned to ebook 1600x2560 */}
                <div className={styles.bgOverlay} style={{ background: layout.overlayColor, opacity: layout.overlayOpacity }} />
                {layout.layers.filter((l) => l.panel === 'front').map((L) => (
                  <div
                    key={L.id}
                    className={`${styles.layer} ${L.id === selectedLayerId ? styles.layerSelected : ''}`}
                    style={{
                      left: `${(L.x - L.w / 2) * 100}%`,
                      top: `${(L.y - L.h / 2) * 100}%`,
                      width: `${L.w * 100}%`,
                      height: `${L.h * 100}%`,
                      fontFamily: L.font, fontSize: `${L.size * (SCALE / 72)}px`, fontWeight: L.weight,
                      fontStyle: L.italic ? 'italic' : 'normal', color: L.color, textAlign: L.align as any,
                      letterSpacing: `${L.letterSpacing}em`, lineHeight: L.lineHeight,
                      display: 'flex', alignItems: 'center',
                      justifyContent: L.align === 'center' ? 'center' : L.align === 'right' ? 'flex-end' : 'flex-start',
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedLayerId(L.id); }}
                  >{L.text}</div>
                ))}
              </div>
            )}
          </div>
          <div className={styles.canvasNote}>
            Print dimensions: {wrapWidthIn.toFixed(2)}" × {wrapHeightIn.toFixed(2)}" wrap, {trimW}" × {trimH}" trim, {bleed}" bleed
          </div>
        </div>

        {/* ── Inspector ── */}
        <div className={styles.inspector}>
          <h3>Layers</h3>
          <ul className={styles.layerList}>
            {layout.layers.map((L) => (
              <li
                key={L.id}
                className={`${styles.layerItem} ${L.id === selectedLayerId ? styles.layerItemActive : ''}`}
                onClick={() => setSelectedLayerId(L.id)}
              >
                <span className={styles.layerKind}>{L.panel}</span>
                <span className={styles.layerText}>{L.text.slice(0, 28)}</span>
              </li>
            ))}
          </ul>

          <h3>Background</h3>
          <div className={styles.section}>
            <button className={styles.btn} onClick={handleRegenBg}>🎨 Regenerate AI background</button>
            <label className={styles.uploadLabel}>
              📁 Upload image
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleBgUpload(f);
                }}
              />
            </label>
            <div className={styles.field}>
              <label>Overlay opacity</label>
              <input
                type="range" min={0} max={1} step={0.05}
                value={layout.overlayOpacity}
                onChange={(e) => updateLayout((p) => ({ ...p, overlayOpacity: parseFloat(e.target.value) }))}
              />
              <span>{Math.round(layout.overlayOpacity * 100)}%</span>
            </div>
            <div className={styles.field}>
              <label>Overlay color</label>
              <input
                type="color"
                value={layout.overlayColor}
                onChange={(e) => updateLayout((p) => ({ ...p, overlayColor: e.target.value }))}
              />
            </div>
            <div className={styles.field}>
              <label>Format</label>
              <select
                value={layout.format || 'paperback'}
                onChange={(e) => updateLayout((p) => ({ ...p, format: e.target.value as 'paperback' | 'hardcover' }))}
              >
                <option value="paperback">Paperback</option>
                <option value="hardcover">Hardcover</option>
              </select>
            </div>
          </div>

          {selectedLayer && (
            <>
              <h3>Layer: {selectedLayer.kind}</h3>
              <div className={styles.section}>
                <div className={styles.field}>
                  <label>Text</label>
                  <textarea
                    value={selectedLayer.text}
                    rows={selectedLayer.kind === 'blurb' ? 6 : 2}
                    onChange={(e) => updateLayer(selectedLayer.id, { text: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <label>Font</label>
                  <select
                    value={selectedLayer.font}
                    onChange={(e) => updateLayer(selectedLayer.id, { font: e.target.value })}
                  >
                    {FONTS.map((f) => (
                      <option key={f.id} value={f.stack}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Size (pt)</label>
                    <input type="number" min={6} max={144} step={1} value={selectedLayer.size}
                      onChange={(e) => updateLayer(selectedLayer.id, { size: parseInt(e.target.value, 10) || 12 })} />
                  </div>
                  <div className={styles.field}>
                    <label>Weight</label>
                    <select value={selectedLayer.weight}
                      onChange={(e) => updateLayer(selectedLayer.id, { weight: parseInt(e.target.value, 10) })}>
                      <option value={300}>Light</option>
                      <option value={400}>Regular</option>
                      <option value={500}>Medium</option>
                      <option value={600}>Semibold</option>
                      <option value={700}>Bold</option>
                      <option value={900}>Black</option>
                    </select>
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Color</label>
                    <input type="color" value={selectedLayer.color}
                      onChange={(e) => updateLayer(selectedLayer.id, { color: e.target.value })} />
                  </div>
                  <div className={styles.field}>
                    <label>Italic</label>
                    <input type="checkbox" checked={selectedLayer.italic}
                      onChange={(e) => updateLayer(selectedLayer.id, { italic: e.target.checked })} />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Align</label>
                    <select value={selectedLayer.align}
                      onChange={(e) => updateLayer(selectedLayer.id, { align: e.target.value as any })}>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Letter spacing</label>
                    <input type="number" step={0.05} value={selectedLayer.letterSpacing}
                      onChange={(e) => updateLayer(selectedLayer.id, { letterSpacing: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>X (0..1)</label>
                    <input type="number" step={0.01} min={0} max={1} value={selectedLayer.x.toFixed(3)}
                      onChange={(e) => updateLayer(selectedLayer.id, { x: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className={styles.field}>
                    <label>Y (0..1)</label>
                    <input type="number" step={0.01} min={0} max={1} value={selectedLayer.y.toFixed(3)}
                      onChange={(e) => updateLayer(selectedLayer.id, { y: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
              </div>
            </>
          )}

          <h3>Publish to KDP</h3>
          <div className={styles.section}>
            <button className={styles.btnPrimary} onClick={() => handlePublish('paperback')}>Publish as Paperback</button>
            <button className={styles.btnPrimary} onClick={() => handlePublish('hardcover')}>Publish as Hardcover</button>
            <button className={styles.btnPrimary} onClick={() => handlePublish('ebook')}>Publish as Kindle eBook</button>
            <p className={styles.note}>
              Uses Amazon credentials from your Integration Settings. Saves as a draft in your KDP
              dashboard — you'll review and click "Publish" there manually.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoverEditor;
